/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FormWrapper from "../../components/ui/FormWrapper";
import Input from "../../components/ui/input/Input";
import Select from "../../components/ui/input/Select";
import EsimPackageSelector from "../../components/voucher-codes/EsimPackageSelector";
import useFetch from "../../hooks/useFetch";
import {
  ICreateVoucherCode,
  IEditVoucherCode,
  IVoucherCode
} from "../../interfaces/endpoints/IVoucherCode";
import {
  createVoucherCode,
  getVoucherCodeById,
  updateVoucherCode
} from "../../services/VoucherCode.service";
import VoucherCodeSchema from "../../utils/validations/VoucherCode.schema";

const rewardTypeOptions = [
  { id: 1, name: "eSIM Package" },
  { id: 2, name: "GjirafaMoney Credits" }
];

const usageModeOptions = [
  { id: 1, name: "Campaign (nje perdorim per perdorues)" },
  { id: 2, name: "EmailLocked (i kycur per nje email)" }
];

type VoucherFormData = ICreateVoucherCode & { isActive?: boolean };

const AddVoucherCode: React.FC<{ editMode?: boolean }> = ({ editMode }) => {
  const { id } = useParams();
  const { state } = useLocation() as { state: { page?: number } | null };
  const navigate = useNavigate();

  const methods = useForm<VoucherFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(VoucherCodeSchema),
    defaultValues: {
      code: "",
      rewardType: undefined,
      usageMode: undefined,
      packageTemplateId: undefined,
      creditAmount: undefined,
      lockedToEmail: "",
      expiresAtUtc: "",
      isActive: true
    }
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors }
  } = methods;

  const rewardType = watch("rewardType");
  const usageMode = watch("usageMode");

  const { response } = useFetch<IVoucherCode, number>(getVoucherCodeById, {
    initRequest: !!editMode && !!id,
    params: Number(id)
  });

  useEffect(() => {
    if (editMode && response?.data) {
      const data = response.data;
      setValue("code", data.code);
      setValue("rewardType", data.rewardType);
      setValue("usageMode", data.usageMode);
      if (data.packageTemplateId !== null && data.packageTemplateId !== undefined) {
        setValue("packageTemplateId", data.packageTemplateId);
      }
      if (data.creditAmount !== null && data.creditAmount !== undefined) {
        setValue("creditAmount", data.creditAmount);
      }
      if (data.lockedToEmail) {
        setValue("lockedToEmail", data.lockedToEmail);
      }
      if (data.expiresAtUtc) {
        setValue(
          "expiresAtUtc",
          dayjs(data.expiresAtUtc).format("YYYY-MM-DDTHH:mm")
        );
      }
      setValue("isActive", data.isActive);
    }
  }, [editMode, response, setValue]);

  const onSubmit: SubmitHandler<VoucherFormData> = async (data) => {
    const payload = {
      code: data.code,
      rewardType: Number(data.rewardType) as 1 | 2,
      usageMode: Number(data.usageMode) as 1 | 2,
      ...(Number(data.rewardType) === 1 && {
        packageTemplateId: Number(data.packageTemplateId)
      }),
      ...(Number(data.rewardType) === 2 && {
        creditAmount: Number(data.creditAmount)
      }),
      ...(Number(data.usageMode) === 2 && {
        lockedToEmail: data.lockedToEmail
      }),
      ...(data.expiresAtUtc && {
        expiresAtUtc: new Date(data.expiresAtUtc).toISOString()
      }),
      isActive: data.isActive ?? true
    };

    if (editMode && id) {
      await updateVoucherCode(Number(id), payload as IEditVoucherCode)
        .then(() => {
          toast.success("Vaucheri u ndryshua me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate(
              `/esim/voucher-codes?pageindex=${state?.page ? state?.page : 1}`,
              { state: ["Esim", "Voucher Codes"] }
            );
          }, 500);
        })
        .catch((err) => {
          const msg =
            err?.response?.status === 409
              ? "Ky kod ekziston tashme."
              : "Ndodhi nje gabim gjate ndryshimit.";
          toast.error(msg, { position: "top-right" });
        });
    } else {
      await createVoucherCode(payload as ICreateVoucherCode)
        .then(() => {
          toast.success("Vaucheri u shtua me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate("/esim/voucher-codes", {
              state: ["Esim", "Voucher Codes"]
            });
          }, 500);
        })
        .catch((err) => {
          const msg =
            err?.response?.status === 409
              ? "Ky kod ekziston tashme."
              : "Ndodhi nje gabim gjate krijimit.";
          toast.error(msg, { position: "top-right" });
        });
    }
  };

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/esim/voucher-codes"
        state={["Esim", "Voucher Codes"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
        onSubmit={handleSubmit(onSubmit)}
        title={editMode ? "Modifiko vaucherin" : "Shto vaucher te ri"}
      >
        <Input
          {...register("code")}
          label="Kodi i vaucherit"
          className="mb-6"
          placeholder="p.sh. WELCOME5EUR"
          error={errors.code?.message}
        />

        <Select
          {...register("rewardType")}
          label="Lloji i shperblimit"
          className="mb-6"
          options={rewardTypeOptions}
          error={errors.rewardType?.message}
        />

        <Select
          {...register("usageMode")}
          label="Menyra e perdorimit"
          className="mb-6"
          options={usageModeOptions}
          error={errors.usageMode?.message}
        />

        {Number(rewardType) === 1 && (
          <EsimPackageSelector
            value={watch("packageTemplateId")}
            onChange={(packageId) => {
              setValue("packageTemplateId", packageId, {
                shouldValidate: true,
                shouldDirty: true
              });
            }}
            error={errors.packageTemplateId?.message}
          />
        )}

        {Number(rewardType) === 2 && (
          <Input
            {...register("creditAmount")}
            label="Shuma e kreditit (EUR)"
            className="mb-6"
            type="number"
            step="0.01"
            placeholder="p.sh. 5.00"
            error={errors.creditAmount?.message}
          />
        )}

        {Number(usageMode) === 2 && (
          <Input
            {...register("lockedToEmail")}
            label="Email (i kycur)"
            className="mb-6"
            type="email"
            placeholder="user@example.com"
            error={errors.lockedToEmail?.message}
          />
        )}

        <Input
          {...register("expiresAtUtc")}
          label="Data e skadimit (opsionale)"
          className="mb-6"
          type="datetime-local"
          error={errors.expiresAtUtc?.message}
        />

        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              {...register("isActive")}
              className="mr-2 w-4 h-4"
            />
            <span className="text-sm font-medium">Aktiv</span>
          </label>
        </div>
      </FormWrapper>
    </FormProvider>
  );
};

export default AddVoucherCode;
